import React from 'react';
import './ReinventLoyaltyHighlight.css';

/**
 * ReinventLoyaltyHighlight - Prominently displays the Reinvent loyalty program
 * R2,000 over 4 months (R500/month) loyalty reward within the debt counselling section.
 *
 * Important compliance notes:
 * - R500 is strictly a loyalty reward, NOT a payment discount
 * - Reinvent is disclosed only as "the debt counsellor we recommend" with no mention
 *   of relationship to Dola (no "subsidiary", "owned by", "partner", etc.)
 * - No mention of loan consolidation anywhere
 */
export default function ReinventLoyaltyHighlight() {
  return (
    <div className="reinvent-highlight" data-testid="reinvent-loyalty-highlight">
      {/* Loyalty badge */}
      <div className="reinvent-highlight__badge" aria-label="Loyalty Reward Program">
        <span className="reinvent-highlight__badge-icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </span>
        <span>Loyalty Reward Program</span>
      </div>

      {/* Main offer card */}
      <div className="reinvent-highlight__offer" data-testid="reinvent-offer-card">
        <div className="reinvent-highlight__amount-row">
          <div className="reinvent-highlight__total">
            <span className="reinvent-highlight__currency">R</span>
            <span className="reinvent-highlight__value" data-testid="reinvent-total-amount">2,000</span>
          </div>
          <div className="reinvent-highlight__period" data-testid="reinvent-period">
            loyalty reward
          </div>
        </div>

        <div className="reinvent-highlight__breakdown">
          <div className="reinvent-highlight__monthly">
            <span className="reinvent-highlight__monthly-amount">R500</span>
            <span className="reinvent-highlight__monthly-label">per month for 4 months</span>
          </div>
        </div>

        <p className="reinvent-highlight__description">
          Receive a <strong>R500 loyalty reward</strong> each month for 4 months when you
          sign up for debt counselling with Reinvent Debt Solutions, the debt counsellor we recommend. This loyalty reward
          is available to qualifying Dola clients as a thank-you for taking steps to manage
          their finances responsibly.
        </p>

        {/* Monthly breakdown visual */}
        <div className="reinvent-highlight__timeline" data-testid="reinvent-timeline" role="list" aria-label="Monthly loyalty reward timeline">
          {[1, 2, 3, 4].map((month) => (
            <div
              key={month}
              className="reinvent-highlight__timeline-step"
              role="listitem"
              aria-label={`Month ${month}: R500 loyalty reward`}
            >
              <div className="reinvent-highlight__timeline-dot" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              </div>
              <div className="reinvent-highlight__timeline-content">
                <span className="reinvent-highlight__timeline-month">Month {month}</span>
                <span className="reinvent-highlight__timeline-reward">R500</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reinvent partner info */}
      <div className="reinvent-highlight__partner" data-testid="reinvent-partner-info">
        <div className="reinvent-highlight__partner-label">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span>
            <strong>Reinvent Debt Solutions</strong> is the debt counsellor we recommend.
          </span>
        </div>
        <p className="reinvent-highlight__partner-detail">
          Their experienced counsellors will guide you through the process and help you
          take control of your financial future.
        </p>
      </div>

      {/* CTA button */}
      <a
        href="https://reinvent.co.za"
        className="btn btn--reinvent reinvent-highlight__cta"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="reinvent-cta"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
        Claim Your Loyalty Reward with Reinvent
      </a>

      <p className="reinvent-highlight__disclaimer">
        The R500 monthly loyalty reward is strictly a loyalty reward for qualifying clients
        and is not a payment discount or reduction of any outstanding balance.
      </p>
    </div>
  );
}
